import {
  type LocationResponse,
  useTaskDetailSuspense,
  useLocation as useTaskLocation,
  useUnassignedFiles,
  type TaskDetailResponse,
  type TaskDetailRequest
} from "@swivu/client-api";
import { EntityChecklists } from "@swivu/entity-detail/src/Checklists.js";
import { EntityContacts } from "@swivu/entity-detail/src/Contacts.js";
import { EntityNotes } from "@swivu/entity-detail/src/notes/Notes.js";
import { EntityReminders } from "@swivu/entity-detail/src/Reminders.js";
import { UnifiedMediaSurface } from "./UnifiedMediaSurface.js";
import {
  cn,
  RichText,
  AddMediaModal,
  LOUD_CHIP,
  META_LABEL,
  tagTone,
  type FileData
} from "@swivu/shared-ui";
import SuspenseWithDelay from "@swivu/shared-ui/src/components/SuspenseWithDelay.js";
import { createContext, useContext, useState } from "react";
import { DateTime, Interval } from "luxon";
import AssignedResources from "./AssignedResources.js";
import AddressDropdown from "./AddressDropdown.js";
import LocationDetail from "./LocationDetail.js";
import ErrorBoundary from "@swivu/shared-ui/src/components/ErrorBoundary.js";

type BaseUrls = {
  checklist: string;
  resource: string;
};
type ViewTaskVariables = TaskDetailRequest;
type ViewTaskProps = ViewTaskVariables & {
  task: TaskDetailResponse;
  timezone: string;
  baseUrls: BaseUrls;
  boardId?: number;
  canAddMedia?: boolean;
  onAnalyze?: (file: FileData) => void;
  advisorsEnabled?: boolean;
};
const ViewTaskContext = createContext<ViewTaskProps | null>(null);

const checkIsTodayAssigned = (
  assigned_resources: TaskDetailResponse["assigned_resources"],
  timezone: string
): boolean => {
  if (!assigned_resources || assigned_resources.length === 0) {
    return false;
  }

  const today = DateTime.now().setZone(timezone).startOf("day");

  return assigned_resources.some((resource) => {
    if (!resource.start_date || !resource.end_date) {
      return false;
    }

    try {
      const startDate = DateTime.fromISO(resource.start_date)
        .setZone(timezone)
        .startOf("day");
      const endDate = DateTime.fromISO(resource.end_date)
        .setZone(timezone)
        .startOf("day");

      const resourceInterval = Interval.fromDateTimes(
        startDate,
        endDate.plus({ days: 1 })
      );

      return resourceInterval.contains(today);
    } catch {
      console.warn("Invalid date in assigned resource:", resource);
      return false;
    }
  });
};

const getTaskLocationId = (task: TaskDetailResponse) =>
  task.location_id ?? task.location ?? null;

const formatLocationAddress = (location: LocationResponse) => {
  const addressParts = [
    location.street_address,
    location.city,
    location.state_or_region,
    location.postal_code,
    location.country
  ].filter(Boolean);

  return {
    formatted_address: addressParts.join(", ") || null,
    lat: location.latitude,
    lon: location.longitude
  };
};

type ProviderProps = ViewTaskVariables & {
  children: React.ReactNode;
  timezone: string;
  baseUrls: BaseUrls;
  boardId?: number;
  canAddMedia?: boolean;
  onAnalyze?: (file: FileData) => void;
  advisorsEnabled?: boolean;
};

const TaskDetailProviderWrapper = (props: ProviderProps) => {
  return (
    <SuspenseWithDelay delay={1000} fallback={<TaskDetailSkeleton />}>
      <ErrorBoundary>
        <TaskDetailProvider {...props} />
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const TaskDetailProvider = ({ children, ...props }: ProviderProps) => {
  const { data } = useTaskDetailSuspense({
    variables: {
      ...props
    }
  });
  return (
    <ViewTaskContext.Provider value={{ ...props, task: data }}>
      {children}
    </ViewTaskContext.Provider>
  );
};

const TaskDetail = () => {
  const { task, timezone, baseUrls } = useTaskDetail();
  const locationId = getTaskLocationId(task);
  const { data: location, isLoading: isLoadingLocation } = useTaskLocation({
    variables: { id: locationId ?? 0 },
    enabled: Boolean(locationId)
  });
  const address = location ? formatLocationAddress(location) : task.address;

  return (
    <div className="min-w-0 max-w-full">
      {task.board_name && (
        <div className="mb-2 flex items-center">
          <span className={META_LABEL}>{task.board_name}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-surface-xl bg-white dark:bg-mono-900 shadow-card-deep">
        <div
          className="tile-color-strip h-2.5"
          style={{ backgroundColor: task.color || "#999999" }}
        />

        <div className="mobile-pref-card p-6 md:py-10 space-y-4">
          <div className="flex flex-col-reverse md:flex-row items-start justify-between gap-4 md:gap-8">
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <h2 className="mobile-pref-section-title text-2xl leading-tight font-black text-mono-900 dark:text-white">
                {task.display_name || "Untitled Task"}
              </h2>
              {!locationId && address?.formatted_address && (
                <AddressDropdown address={address} />
              )}
            </div>

            {task.status_value && (
              <span className={cn(LOUD_CHIP, "bg-tile-primary")}>
                {task.status_value}
              </span>
            )}
          </div>

          {locationId && (
            <LocationDetail location={location} isLoading={isLoadingLocation} />
          )}

          {task.description && (
            <div className="py-4">
              <RichText
                className="mobile-pref-card-body font-body text-xl text-mono-700 dark:text-mono-300"
                html={task.description}
              />
            </div>
          )}

          <div className="mobile-pref-card-body flex flex-wrap gap-x-6 gap-y-4 py-2 text-sm">
            {task.team_name && (
              <div className="flex flex-col gap-1">
                <span className={META_LABEL}>Team</span>
                <span
                  className={cn(
                    LOUD_CHIP,
                    "self-start",
                    !task.team_color && tagTone(task.team_name)
                  )}
                  style={
                    task.team_color
                      ? { backgroundColor: task.team_color }
                      : undefined
                  }
                >
                  {task.team_name}
                </span>
              </div>
            )}

            <TaskAttribute title="Category" value={task.category_value} />
            <TaskAttribute title="Phase" value={task.phase_value} />
            <TaskAttribute title="Budget ID" value={task.budget_number} />
          </div>

          <LinkedTasks />

          {task.tag_values && task.tag_values.length > 0 && (
            <div className="my-2">
              <div className="flex flex-wrap gap-2">
                {task.tag_values
                  .filter((t): t is string => !!t)
                  .map((tag, index) => (
                    <span key={index} className={cn(LOUD_CHIP, tagTone(tag))}>
                      {tag}
                    </span>
                  ))}
              </div>
            </div>
          )}
          <div className="h-4" />
          <AssignedResources
            isTodayAssigned={checkIsTodayAssigned(
              task.assigned_resources,
              timezone
            )}
            assigned_resources={task.assigned_resources}
            timezone={timezone}
            resourceUrl={baseUrls.resource}
          />
        </div>
      </div>
    </div>
  );
};

const TaskAttribute = ({
  title,
  value
}: {
  title: string;
  value?: string | null;
}) => {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className={META_LABEL}>{title}</span>
      <span className={cn(LOUD_CHIP, "self-start", tagTone(value))}>
        {value}
      </span>
    </div>
  );
};

const LinkedTasks = () => {
  const { task, boardId, canAddMedia, onAnalyze, advisorsEnabled, ...props } =
    useTaskDetail();
  const [showAddMedia, setShowAddMedia] = useState(false);

  const { data: unassignedFiles, isLoading: isLoadingUnassigned } =
    useUnassignedFiles({
      enabled: !!(canAddMedia && !!boardId && props.api === "wf")
    });

  const variables = {
    ...props,
    id: props.id,
    entity_type: "task" as const,
    title: task.display_name ?? ""
  };

  const canAdd = canAddMedia && !!boardId && task.can_create_file !== false;

  return (
    <>
      <div className="has-[&>*]:md:py-8 has-[&>*]:py-4 grid w-full grid-cols-2 gap-6 md:grid-cols-3 md:gap-12">
        <EntityContacts {...variables} />
        {task?.notes_count && task?.notes_count > 0 ? (
          <EntityNotes {...variables} />
        ) : null}
        {task?.checklist_count && task?.checklist_count > 0 ? (
          <EntityChecklists {...variables} baseUrl={props.baseUrls.checklist} />
        ) : null}
        <UnifiedMediaSurface
          {...variables}
          task={task}
          canAdd={canAdd}
          onAdd={() => setShowAddMedia(true)}
          onAnalyze={onAnalyze}
          advisorsEnabled={advisorsEnabled}
        />
        <EntityReminders {...variables} timezone={props.timezone} />
      </div>

      {canAdd && (
        <AddMediaModal
          entity={{
            type: "task",
            id: props.id,
            name: task.display_name ?? "Task"
          }}
          boardId={boardId}
          open={showAddMedia}
          onOpenChange={setShowAddMedia}
          unassignedFiles={unassignedFiles}
          isLoadingUnassigned={isLoadingUnassigned}
        />
      )}
    </>
  );
};

const TaskDetailSkeleton = () => {
  return (
    <div
      className="mt-4 max-w-full animate-pulse"
      role="status"
      aria-busy
      aria-live="polite"
    >
      <div className="overflow-hidden rounded-surface-xl bg-white dark:bg-mono-900 shadow-card-deep">
        <div className="bg-mono-200 dark:bg-mono-700 h-2.5" />

        <div className="space-y-4 p-6">
          <div className="flex items-start justify-between">
            <div
              className={cn(
                "bg-mono-200 dark:bg-mono-700 h-7 w-48",
                "rounded-surface-xs"
              )}
            />
            <div className="bg-mono-200 dark:bg-mono-700 h-8 w-24 rounded-full" />
          </div>

          <div className="flex gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div
                  className={cn(
                    "bg-mono-200 dark:bg-mono-700 h-3 w-16",
                    "rounded-surface-2xs"
                  )}
                />
                <div
                  className={cn(
                    "bg-mono-200 dark:bg-mono-700 h-5 w-24",
                    "rounded-surface-xs"
                  )}
                />
              </div>
            ))}
          </div>

          <div
            className={cn(
              "bg-mono-200 dark:bg-mono-700 h-20",
              "rounded-surface-xs"
            )}
          />

          <div
            className={cn(
              "bg-mono-200 dark:bg-mono-700 h-6 w-72",
              "rounded-surface-xs"
            )}
          />

          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "bg-mono-100 dark:bg-mono-800 flex flex-col gap-1.5 p-2",
                  "rounded-surface-lg"
                )}
              >
                <div
                  className={cn(
                    "bg-mono-200 dark:bg-mono-700 h-6 w-8",
                    "rounded-surface-2xs"
                  )}
                />
                <div
                  className={cn(
                    "bg-mono-200 dark:bg-mono-700 h-3 w-16",
                    "rounded-surface-2xs"
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const useTaskDetail = () => {
  const context = useContext(ViewTaskContext);
  if (!context) {
    throw new Error("useTaskDetail must be used within a TaskDetailProvider");
  }
  return context;
};

TaskDetail.useTaskDetail = useTaskDetail;
TaskDetail.Provider = TaskDetailProviderWrapper;
TaskDetail.Skeleton = TaskDetailSkeleton;

export default TaskDetail;
